// hooks/useSubmissionHandler.ts
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { chunkArray } from '../utils/dataProcessing';

interface SubmissionHandlerProps {
  data: any[];
  selectedRows: string[];
  selectedDependentes: { [key: string]: string[] };
  selecionado: string;
  departamento: string;
  vigencia: string;
  setData: (value: any[] | ((prev: any[]) => any[])) => void;
  setSelectedRows: (value: string[]) => void;
  setSelectedDependentes: (value: { [key: string]: string[] }) => void;
  setSubmissionResponse: (value: any) => void;
  setLoading: (value: boolean) => void;
}

interface SubmissionData {
  planoNow: any;
  planoOne: any;
}

interface SubmissionHandlerReturn {
  handleSubmit: (submitData: any) => Promise<void>;
}

export function useSubmissionHandler(
  props: SubmissionHandlerProps,
): SubmissionHandlerReturn {
  const {
    data,
    selectedRows,
    selectedDependentes,
    selecionado,
    departamento,
    vigencia,
    setData,
    setSelectedRows,
    setSelectedDependentes,
    setSubmissionResponse,
    setLoading,
  } = props;

  const processSingleBatch = async (
    batchData: any[], // Array com 75 registros
    batchIndex: number,
    totalBatches: number,
    submitData: SubmissionData,
  ): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    batchIndex: number;
    processedCount: number;
  }> => {
    const urlBase = process.env.REACT_APP_API_EVO;
    const token = process.env.REACT_APP_TOKEN_EVO;

    // Enviar TODOS os 75 registros de uma vez
    const batchFormData = {
      Data: batchData, // Array completo com até 75 registros
      selecionado,
      selectedPlans: submitData,
      departamento,
      inicioVigencia: vigencia,
    };

    try {
      const startTime = Date.now();

      const response = await axios.post(
        `${urlBase}/include/odontogroup`,
        batchFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 1200000, // 20 minutos por lote (pode ser muitos registros)
        },
      );

      const duration = Date.now() - startTime;

      if (response.status === 200) {
        return {
          success: true,
          data: response.data,
          batchIndex,
          processedCount: batchData.length,
        };
      } else {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
    } catch (error: any) {
      console.error(`❌ Erro no lote ${batchIndex + 1}:`, error);

      const apiError = error.response?.data;
      const errorMessage =
        apiError?.message || error.message || 'Erro desconhecido';

      return {
        success: false,
        error: errorMessage,
        batchIndex,
        processedCount: 0,
      };
    }
  };

  const processBatchesInParallel = async (
    batches: any[][], // Array de arrays, cada sub-array tem 75 registros
    submitData: SubmissionData,
    concurrency = 1,
    delayBetweenGroups = 8000,
  ): Promise<{
    successful: any[];
    failed: any[];
    responses: any[];
    errors: string[];
  }> => {
    const results = {
      successful: [] as any[],
      failed: [] as any[],
      responses: [] as any[],
      errors: [] as string[],
    };

    const totalBatches = batches.length;
    const totalRegistros = batches.reduce(
      (acc, batch) => acc + batch.length,
      0,
    );

    // Dividir batches em grupos de acordo com a concorrência
    const batchGroups = chunkArray(batches, concurrency);
    const totalGroups = batchGroups.length;

    for (let groupIndex = 0; groupIndex < batchGroups.length; groupIndex++) {
      const currentGroup = batchGroups[groupIndex];
      const groupBatchNumbers = currentGroup.map(
        (_, i) => groupIndex * concurrency + i + 1,
      );

      // Criar promessas para execução paralela dos lotes
      const groupPromises = currentGroup.map(
        async (batchData, batchIndexInGroup) => {
          const globalBatchIndex = groupIndex * concurrency + batchIndexInGroup;
          return processSingleBatch(
            batchData,
            globalBatchIndex,
            totalBatches,
            submitData,
          );
        },
      );

      try {
        // Executar todos os lotes do grupo em paralelo
        const groupResults = await Promise.allSettled(groupPromises);

        // Processar resultados do grupo
        let groupSuccessCount = 0;
        let groupFailCount = 0;
        let groupProcessedRecords = 0;
        const groupErrors: string[] = [];

        groupResults.forEach((result, index) => {
          const globalBatchIndex = groupIndex * concurrency + index;
          const batchData = currentGroup[index];

          if (result.status === 'fulfilled' && result.value.success) {
            results.successful.push(...batchData);
            results.responses.push(result.value.data);
            groupSuccessCount++;
            groupProcessedRecords += result.value.processedCount;
          } else {
            results.failed.push(...batchData);
            groupFailCount++;

            const error =
              result.status === 'fulfilled'
                ? result.value.error
                : result.reason?.message || 'Falha crítica';

            const errorMsg = `Lote ${globalBatchIndex + 1} (${
              batchData.length
            } registros): ${error}`;
            results.errors.push(errorMsg);
            groupErrors.push(errorMsg);
          }
        });

        // Delay entre grupos (exceto no último)
        if (groupIndex < batchGroups.length - 1) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenGroups));
          toast.dismiss('delay-toast');
        }
      } catch (groupError: any) {
        console.error(
          `💥 Erro crítico no grupo ${groupIndex + 1}:`,
          groupError,
        );
        // Adicionar todos os lotes do grupo como falha
        currentGroup.forEach((batchData, index) => {
          results.failed.push(...batchData);
          results.errors.push(
            `Grupo ${groupIndex + 1}, Lote ${
              groupIndex * concurrency + index + 1
            } ` + `(${batchData.length} registros): Falha crítica do grupo`,
          );
        });
      }
    }

    return results;
  };

  const handleSubmit = useCallback(
    async (submitData: SubmissionData) => {
      try {
        // Preparar dados selecionados
        const selectedTitulares = data
          .filter(titular => selectedRows.includes(titular.id))
          .map(titular => {
            const dependentesSelecionados =
              titular.dependentes?.filter(dep =>
                selectedDependentes[titular.id]?.includes(dep.id),
              ) || [];

            return {
              ...titular,
              dependentes: dependentesSelecionados,
            };
          });

        if (selectedTitulares.length === 0) {
          toast.error('Nenhum titular selecionado para envio');
          return;
        }

        // Dividir em lotes de 200 registros (cada lote será enviado de uma vez)
        const dataBatches = chunkArray(selectedTitulares, 200);

        // Processar lotes em paralelo (1 lote de 200 registros simultâneos)
        const results = await processBatchesInParallel(
          dataBatches,
          submitData,
          1, // 1 lote simultâneo
          8000, // 8 segundos entre grupos
        );

        // Resultado final
        const successCount = results.successful.length;
        const failedCount = results.failed.length;
        const totalCount = successCount + failedCount;
        const successRate =
          totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0;

        if (failedCount === 0) {
          toast.success(
            `🎉 Processamento concluído com 100% de sucesso!\n` +
              `✅ ${successCount} registros processados\n` +
              `📦 ${dataBatches.length} lotes de 50 registros cada\n` +
              { duration: 10000 },
          );

          // Limpar dados após sucesso total
          setData([]);
          setSelectedRows([]);
          setSelectedDependentes({});

          setSubmissionResponse({
            success: true,
            processed: successCount,
            total: totalCount,
            successRate: 100,
            batchProcessing: true,
            batchSize: 200,
            concurrency: 1,
            totalBatches: dataBatches.length,
            totalGroups: Math.ceil(dataBatches.length / 1),
            responses: results.responses,
            timestamp: new Date().toISOString(),
          });
        } else {
          toast(
            `⚠️ Processamento concluído com ${successRate}% de sucesso\n` +
              `✅ Sucessos: ${successCount} registros\n` +
              `❌ Falhas: ${failedCount} registros\n` +
              `📦 ${dataBatches.length} lotes processados\n` +
              `⚡ Cada lote com até 200 registros por requisição`,
            { duration: 8000, icon: '⚠️' },
          );

          setSubmissionResponse({
            success: false,
            processed: successCount,
            failed: failedCount,
            total: totalCount,
            successRate,
            batchProcessing: true,
            batchSize: 200,
            concurrency: 1,
            totalBatches: dataBatches.length,
            totalGroups: Math.ceil(dataBatches.length / 1),
            errors: results.errors,
            responses: results.responses,
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error: any) {
        console.error('💥 Erro geral no processamento massivo:', error);
        toast.error(
          `💥 Erro crítico no processamento massivo: ${error.message}`,
        );

        setSubmissionResponse({
          success: false,
          error: error.message,
          batchProcessing: true,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [
      data,
      selectedRows,
      selectedDependentes,
      selecionado,
      departamento,
      vigencia,
      setData,
      setSelectedRows,
      setSelectedDependentes,
      setSubmissionResponse,
    ],
  );

  return {
    handleSubmit,
  };
}
